import Link from 'next/link'
import { FC, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import type { SubscriptionSummary } from 'common/types/responses'
import Button from 'components/base/forms/Button'
import {
  UserAddIcon,
  UserSettingsIcon,
  EditIcon,
  ArrowCircleDownIcon,
  UsersIcon,
} from 'components/icons'
import ModalDefault from 'components/base/ModalDefault'
import CreateEditGroup from 'components/team/CreateEditGroup'
import ReassignCustomer from 'components/customers/ReassignCustomer'
import { usePermission } from 'common/services/useUser'
import { useGetGroupQuery } from 'common/services/useGroup'
import { Permission } from 'common/types/enums'

export const downloadCSV = (csvString: string, filename: string) => {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('download', filename)
  a.click()
  window.URL.revokeObjectURL(url)
}

type SubscriptionTableActionsProps = {
  groupId?: number
  showSelection: boolean
  setShowSelection: (v: boolean) => void
  selectedSubscriptions: SubscriptionSummary[]
  setSelectedSubscriptions: (v: SubscriptionSummary[]) => void
  hasSubscriptionResults: boolean
  onExportCSV: () => void
  isExporting: boolean
  onReassignComplete: () => void
}

const SubscriptionTableActions: FC<SubscriptionTableActionsProps> = ({
  groupId,
  hasSubscriptionResults,
  isExporting,
  onExportCSV,
  onReassignComplete,
  selectedSubscriptions,
  setSelectedSubscriptions,
  setShowSelection,
  showSelection,
}) => {
  const canEdit = usePermission(Permission.WRITE)
  const { data: groupDetail } = useGetGroupQuery(
    groupId ? { id: groupId } : skipToken,
  )

  const [showEditGroup, setShowEditGroup] = useState(false)
  const [showReassignment, setShowReassignment] = useState(false)
  return (
    <>
      {!showSelection && (
        <div className='d-flex justify-content-end mb-5 gap-4'>
          {canEdit && (
            <Link
              href={`/customers/create${groupId ? `?groups=${groupId}` : ''}`}
            >
              <Button>
                <div className='d-flex gap-2 align-items-center'>
                  Add Customer
                  <UserAddIcon className='text-white' />
                </div>
              </Button>
            </Link>
          )}
          {hasSubscriptionResults && canEdit && (
            <Button theme='tertiary'>
              <div
                onClick={() => setShowSelection(true)}
                className='d-flex gap-2 align-items-center'
              >
                Manage Customers
                <UserSettingsIcon className='text-white' />
              </div>
            </Button>
          )}
          {groupId ? (
            <Button theme='secondary' onClick={() => setShowEditGroup(true)}>
              <div className='d-flex gap-2 align-items-center'>
                Edit Group
                <EditIcon className='text-dark300' />
              </div>
            </Button>
          ) : (
            <Button
              theme='secondary'
              onClick={onExportCSV}
              disabled={isExporting}
            >
              <div className='d-flex gap-2 align-items-center'>
                Export CSV
                <ArrowCircleDownIcon className='text-dark300' />
              </div>
            </Button>
          )}
        </div>
      )}

      {showSelection && (
        <div className='d-flex align-items-center justify-content-end mb-5 gap-4'>
          <span>
            <strong>{selectedSubscriptions.length}</strong> selected
          </span>
          <Button
            style={{ width: 140 }}
            onClick={() => {
              setShowSelection(false)
              setSelectedSubscriptions([])
            }}
            theme='secondary'
          >
            Cancel
          </Button>
          <Button
            style={{ width: 140 }}
            disabled={selectedSubscriptions.length === 0}
            onClick={() => setShowReassignment(true)}
          >
            <div className='d-flex gap-2 align-items-center'>
              Reassign
              <UsersIcon className='text-white' />
            </div>
          </Button>
        </div>
      )}

      {!!groupDetail && (
        <ModalDefault
          isOpen={showEditGroup}
          title='Edit Group'
          onDismiss={() => setShowEditGroup(false)}
          toggle={() => setShowEditGroup(!showEditGroup)}
        >
          <CreateEditGroup
            onComplete={() => setShowEditGroup(false)}
            group={groupDetail}
          />
        </ModalDefault>
      )}

      {showSelection && (
        <ReassignCustomer
          from={groupId}
          isOpen={showReassignment}
          setIsOpen={setShowReassignment}
          selectedCustomers={selectedSubscriptions}
          onComplete={() => {
            onReassignComplete()
            setSelectedSubscriptions([])
            setShowSelection(false)
          }}
        />
      )}
    </>
  )
}

export default SubscriptionTableActions
